import { type AppointmentType, type ExpandableFieldItem } from '@features/appointment-types';
import {
  doCptCodesMatch,
  getTelehealthForModality,
  normalizeCptCodes,
  type VisitModality,
} from '@utils/modalityPlaceOfService';

export interface TypeSessionDefaults {
  modality: VisitModality;
  telehealth: boolean;
  placeOfService: string | null;
  cptCodes: string[];
  useJotmeet: boolean;
}

/**
 * Resolve an appointment type's session defaults. Legacy types predate
 * default_modality, so a telehealth flag without a modality means video, and
 * JotMeet is only meaningful on video visits.
 */
export const resolveTypeSessionDefaults = (
  appointmentType: AppointmentType,
): TypeSessionDefaults => {
  const modality =
    appointmentType.default_modality ?? (appointmentType.telehealth ? 'video' : null);
  return {
    modality,
    telehealth: getTelehealthForModality(modality),
    placeOfService: appointmentType.default_place_of_service ?? null,
    cptCodes: normalizeCptCodes(appointmentType.default_cpt_codes),
    useJotmeet: Boolean(appointmentType.default_use_jotmeet && modality === 'video'),
  };
};

const formsMatchTypeDefaults = (
  appointmentType: AppointmentType,
  forms: ExpandableFieldItem[],
): boolean => {
  const defaultRules = appointmentType.default_form_rules ?? [];
  if (defaultRules.length !== forms.length) return false;
  return defaultRules.every((rule, index) => {
    const form = forms[index];
    return (
      form && form.formId === rule.form_definition_id && (form.offset ?? '') === (rule.offset ?? '')
    );
  });
};

const remindersMatchTypeDefaults = (
  appointmentType: AppointmentType,
  reminders: ExpandableFieldItem[],
): boolean => {
  const defaultRules = appointmentType.default_reminder_rules ?? [];
  if (defaultRules.length !== reminders.length) return false;
  return defaultRules.every((rule, index) => {
    const reminder = reminders[index];
    // Reminder items carry title-or-template-id as their name (see
    // populateRemindersFromDefaults), so compare against the same fallback.
    return (
      reminder &&
      reminder.name === (rule.title || rule.reminder_template_id) &&
      (reminder.offset ?? '') === (rule.offset ?? '')
    );
  });
};

export interface AppointmentTypeDeviationInput {
  /**
   * The FULL appointment type record. Callers must pass a record from the
   * loaded types list (not a denormalized stub off an appointment, which has
   * no default_* fields) or the comparison is meaningless — pass undefined to
   * report no deviation instead.
   */
  appointmentType: AppointmentType | undefined;
  duration: number | undefined;
  modality: VisitModality;
  placeOfService: string | null;
  cptCodes: string[] | null;
  useJotmeet: boolean | null;
  forms: ExpandableFieldItem[];
  reminders: ExpandableFieldItem[];
}

/**
 * Whether the appointment being submitted deviates from its type's defaults.
 * Drives the persisted `override_defaults` flag: overriding appointments stop
 * following their type — the encounter view resolves billing place-of-service
 * from the appointment alone, and the edit form stops re-syncing values from
 * the type. Deliberately excludes labels, which are appointment-owned and
 * never participate in override_defaults.
 */
export const appointmentOverridesTypeDefaults = ({
  appointmentType,
  duration,
  modality,
  placeOfService,
  cptCodes,
  useJotmeet,
  forms,
  reminders,
}: AppointmentTypeDeviationInput): boolean => {
  if (!appointmentType) return false;

  if (
    appointmentType.default_duration &&
    duration !== undefined &&
    duration !== appointmentType.default_duration
  ) {
    return true;
  }

  const defaults = resolveTypeSessionDefaults(appointmentType);
  if (
    modality !== defaults.modality ||
    (placeOfService ?? null) !== defaults.placeOfService ||
    !doCptCodesMatch(cptCodes, defaults.cptCodes) ||
    Boolean(useJotmeet) !== defaults.useJotmeet
  ) {
    return true;
  }

  return (
    !formsMatchTypeDefaults(appointmentType, forms) ||
    !remindersMatchTypeDefaults(appointmentType, reminders)
  );
};
